import {
  SEARCH_DEFAULT_FILTERS,
  SEARCH_DEFAULT_POOL_EXPERIMENT,
} from '@constants/SearchDefaultOptions';
import { useSearchContext } from '@context/SearchContext2025';
import MobileSRPFilters from '@components/SearchExperiment/SRPFilters/MobileSRPFilters';
import useIsMobile from '@hooks/useIsMobile';
import Tabs from '@solar/common/Tabs/Tabs';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import SearchControls from '@components/SearchExperiment/SearchControls/SearchControls';
import hamburger from '@assets/images/hamburger-menu.svg';
import Image from 'next/image';
import Link from 'next/link';
import rpLogo from '@assets/images/rp-logo-mark.svg';
import DesktopMenu from '@components/SearchExperiment/Header/DesktopMenu';
import closeButtonBlack from '@assets/images/close-black-light.svg';
import cn from '@helpers/className';
import MobileMenuV2 from '@components/Menu/MobileMenu/MobileMenuV2';
import Modal from '@components/common/Modal/Modal';
import CurrencyList from '@components/SearchExperiment/CurrencyList/CurrencyList';
import useLoginAndCart from '@hooks/useLoginAndCart';
import ModalContainer from '@components/AuthForm/ModalContainer';
import CookiesBanner from '@components/common/CookiesBanner/CookiesBanner';
import { CurrencyData } from '@customTypes/currency';
import useScrollLock from '@hooks/useScrollLock';
import { useAmplitudeApplyProductCategoryTracker } from '@hooks/useAmplitudeTrackers';
import { generateProductTabsHref } from '@helpers/filters';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import LoginAndCartMenuItem from './LoginAndCartMenuItem';

function HamburgerButton({
  showMenu,
  setShowMenu,
}: {
  showMenu: boolean;
  setShowMenu: Dispatch<SetStateAction<boolean>>;
}) {
  const handleOnHamburgerClick = () => {
    setShowMenu((prevState) => !prevState);
  };

  return (
    <button
      type="button"
      onClick={handleOnHamburgerClick}
      className={cn({
        'flex items-center h-6 justify-center relative w-6': true,
        'hover:bg-rp-light-gray-4 h-10 w-10 rounded-full': showMenu,
      })}
    >
      {showMenu ? (
        <Image src={closeButtonBlack} alt="close menu" />
      ) : (
        <Image src={hamburger} alt="open menu" layout="fill" />
      )}
    </button>
  );
}

function Logo() {
  return (
    <div className="relative h-15px w-160px desktop:h-7 desktop:w-70">
      <Link href="/">
        <a>
          <Image src={rpLogo} alt="ResortPass" layout="fill" />
        </a>
      </Link>
    </div>
  );
}

type Props = {
  currencyData: CurrencyData | undefined;
};

export default function Header({ currencyData }: Props) {
  const {
    search,
    searchParams,
    searchParams: { product_category: productCategory },
    setProductCategory,
  } = useSearchContext();
  const isMobile = useIsMobile();
  const [fireOnChange, setFireOnChange] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isCurrencySelectorModalOpen, setIsCurrencySelectorModalOpen] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState(false); // For SSR safety
  const {
    isLoginMenuOpen,
    setIsLoginMenuOpen,
    isSignUpClicked,
    setIsSignUpClicked,
    showCookiesConsentBanner,
    handleAuthClick,
    isLoginButtonClicked,
    setIsLoginButtonClicked,
  } = useLoginAndCart();

  const { defaultSearchPoolExperiment } = useFeatureFlagContext();

  // Lock body scroll when drawer is open
  useScrollLock(isLoginButtonClicked);

  const amplitudeProductCategoryTracker = useAmplitudeApplyProductCategoryTracker();
  const handleOnCategorySelect = (category: string) => {
    const lc = category.toLowerCase();
    setProductCategory(lc === 'all' ? '' : lc);
    setFireOnChange(true);
    amplitudeProductCategoryTracker(lc);
  };

  const openCurrencySelectorModal = () => {
    setIsCurrencySelectorModalOpen(true);
    setShowMenu(false);
  };

  useEffect(() => {
    setIsMounted(true); // Only runs on client
  }, []);

  // Handle body scroll locking when desktop menu is open
  useEffect(() => {
    if (!isMounted) return; // Skip during SSR

    if (showMenu && !isMobile) {
      // Disable scrolling on body
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = '';
    }

    // Cleanup on unmount or when showMenu/isMobile changes
    // eslint-disable-next-line consistent-return
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu, isMobile, isMounted]);

  useEffect(() => {
    if (fireOnChange) {
      search();
      setFireOnChange(false);
    }
  }, [fireOnChange, search]);

  return (
    <>
      {/*
      // ---------------------------------------------------------------
      // Desktop Layout ------------------------------------------------
      // ---------------------------------------------------------------
      */}
      <div
        id="desktop-header"
        className="hidden desktop:flex flex-col w-full sticky top-0 bg-solar-surface-primary z-400"
      >
        <div
          className="border-b border-solar-secondary flex items-center justify-between px-12 z-300"
          style={{ height: '87px' }}
        >
          <div className="flex flex-1 items-center space-x-spacing-md">
            <HamburgerButton showMenu={showMenu} setShowMenu={setShowMenu} />
            <Logo />
          </div>

          <div className="flex-1 min-w-380 max-w-536 w-full relative">
            <SearchControls
              layout="row-same-width"
              dropdownLayout="full"
              buttonVariant="no-button"
              autoSearch
            />
          </div>

          <div className="flex flex-1 justify-end">
            <LoginAndCartMenuItem
              isLoginMenuOpen={isLoginMenuOpen}
              setIsLoginMenuOpen={setIsLoginMenuOpen}
              isMobile={isMobile}
              setIsSignUpClicked={setIsSignUpClicked}
              onAuthButtonClick={handleAuthClick}
              setShowDesktopMenu={setShowMenu}
            />
          </div>
        </div>

        {/* Tabs: visible on both */}
        {!showMenu ? (
          <Tabs
            defaultValue={
              productCategory || productCategory.length ? productCategory.toLowerCase() : 'all'
            }
            onSelect={handleOnCategorySelect}
            size="md"
          >
            <Tabs.List className="px-4 justify-center space-x-spacing-xl">
              {SEARCH_DEFAULT_FILTERS.map((category) => (
                <Tabs.Tab
                  key={category.name}
                  icon={!isMobile ? category.icon : undefined}
                  iconPosition={!isMobile ? 'left' : undefined}
                  value={category.name}
                  href={generateProductTabsHref(searchParams, category.name)}
                  className="capitalize flex-none w-auto"
                >
                  {category.name}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        ) : null}
      </div>

      {/* END Desktop Layout */}

      {/*
      // ---------------------------------------------------------------
      // Mobile Layout -------------------------------------------------
      // ---------------------------------------------------------------
      */}
      <div className="desktop:hidden border-b border-solar-secondary flex items-center justify-between px-spacing-md py-2 z-400">
        <div className="flex items-center gap-x-2">
          <HamburgerButton showMenu={showMenu} setShowMenu={setShowMenu} />
          <Logo />
        </div>

        <div className="z-400">
          <LoginAndCartMenuItem
            isLoginMenuOpen={isLoginMenuOpen}
            setIsLoginMenuOpen={setIsLoginMenuOpen}
            isMobile={isMobile}
            setIsSignUpClicked={setIsSignUpClicked}
            onAuthButtonClick={handleAuthClick}
            setShowDesktopMenu={setShowMenu}
          />
        </div>
      </div>

      <div className="desktop:hidden sticky top-0 z-300 bg-solar-surface-primary border-b border-solar-secondary">
        <div className="pt-spacing-md px-spacing-md">
          <SearchControls
            layout="row-same-width"
            dropdownLayout="full"
            buttonVariant="no-button"
            autoSearch
          />
        </div>

        {/* Tabs: visible on both */}
        {!showMenu ? (
          <Tabs
            defaultValue={
              productCategory || productCategory.length ? productCategory.toLowerCase() : 'all'
            }
            onSelect={handleOnCategorySelect}
            size="md"
          >
            <Tabs.List className="px-4 desktop:justify-center desktop:space-x-spacing-xl">
              {(defaultSearchPoolExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                ? SEARCH_DEFAULT_POOL_EXPERIMENT
                : SEARCH_DEFAULT_FILTERS
              ).map((category) => (
                <Tabs.Tab
                  key={category.name}
                  icon={!isMobile ? category.icon : undefined}
                  iconPosition={!isMobile ? 'left' : undefined}
                  value={category.name}
                  href={generateProductTabsHref(searchParams, category.name)}
                  className="capitalize flex-1 w-full"
                >
                  {category.name}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        ) : null}

        <MobileSRPFilters />
      </div>

      {/* END Mobile Layout */}

      {showMenu ? (
        <>
          <div className="hidden desktop:block">
            <DesktopMenu
              showDesktopMenu
              setShowDesktopMenu={setShowMenu}
              openCurrencySelectorModal={openCurrencySelectorModal}
            />
          </div>

          <div className="block desktop:hidden">
            <MobileMenuV2
              showMobileMenu
              setShowMobileMenu={setShowMenu}
              openCurrencySelectorModal={openCurrencySelectorModal}
            />
          </div>
        </>
      ) : null}

      {showCookiesConsentBanner && <CookiesBanner />}

      {isLoginButtonClicked && (
        <>
          <div className="fixed w-full h-full z-500 bg-black opacity-50 top-0" />
          <div className="-translate-x-1/2 -translate-y-1/2 fixed flex flex-col items-center justify-between left-1/2 rounded-xl top-1/2 transform w-full z-500 h-full overflow-y-auto d:h-auto">
            <ModalContainer
              setIsLoginButtonClicked={setIsLoginButtonClicked}
              isSignUpButtonClicked={isSignUpClicked}
            />
          </div>
        </>
      )}

      <div className="h-full d:h-auto">
        <Modal
          isOpen={isCurrencySelectorModalOpen}
          closePopup={() => setIsCurrencySelectorModalOpen(false)}
          childrenClass="pb-8 pt-0"
          containerzIndex="z-500"
          showCloseButton
          desktopWidth="d:w-auto d:max-w-856"
          overlayClasses="z-500"
        >
          <CurrencyList
            currencyData={currencyData}
            setIsCurrencySelectorModalOpen={setIsCurrencySelectorModalOpen}
          />
        </Modal>
      </div>
    </>
  );
}
